import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useDemands } from '@contexts/DemandsContext';
import { useClientCatalog } from '@hooks/useClientCatalog';
import { useNotificationBadgeCountsByClient } from '@hooks/useNotificationBadgeCountsByClient';
import ClientSelector from './ClientSelector';

/**
 * Filtro de condomínio — sempre escolha única em todas as páginas que o usam (Demandas,
 * Relatórios, Sugestões). Métricas/board misturando vários condomínios de uma vez não fazem
 * sentido pra análise nem pra operação do dia a dia; quem decide QUAL condomínio fica selecionado
 * ao entrar numa página é `ensureSingleClientSelected` (`DemandsContext.tsx`), chamado pelos
 * loaders de cada página — este componente só reflete/edita `selectedClients` (0 ou 1 item).
 *
 * A UI em si (dropdown com busca) e o carregamento da lista de condomínios foram extraídos pra
 * `ClientSelector`/`useClientCatalog` (02/09) — reutilizáveis fora do `DemandsContext`, usados
 * diretamente pelas páginas de Checklist/Patrimônio. Este componente é só a "casca" que os liga
 * ao contexto de Demandas.
 */
const ClientFilter: React.FC = () => {
  const { selectedClients, setSelectedClients } = useDemands();
  const { clients, loading } = useClientCatalog();
  const { countsByClient } = useNotificationBadgeCountsByClient();

  // Achata pro formato que ClientSelector consome (clientId → total) — o detalhamento completo
  // por categoria não cabe/não faz sentido num badge de 2 dígitos no seletor.
  const pendingByClient = useMemo(() => {
    if (!countsByClient) return undefined;
    return Object.fromEntries(Object.entries(countsByClient).map(([clientId, counts]) => [clientId, counts.total]));
  }, [countsByClient]);

  return (
    <Box sx={{ mr: { xs: 0, md: 2 }, width: { xs: '100%', md: 'auto' } }}>
      <ClientSelector
        clients={clients}
        loading={loading}
        value={selectedClients[0] || ''}
        onChange={(clientId) => setSelectedClients(clientId ? [clientId] : [])}
        pendingByClient={pendingByClient}
      />
    </Box>
  );
};

export default ClientFilter;
